import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, X, Upload, Image as ImageIcon } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Card, CardContent } from '../../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useToast } from '../../hooks/use-toast';
import { getVehicle, createVehicle, updateVehicle, uploadImages } from '../../services/api';

export const VehicleForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { toast } = useToast();
  const isEdit = id !== 'new';
  const [uploading, setUploading] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const [formData, setFormData] = useState({
    make: '',
    model: '',
    year: new Date().getFullYear(),
    price: '',
    mileage: '',
    condition: 'Excellent',
    images: ['', ''],
    features: [''],
    description: '',
    transmission: 'Automatic',
    fuelType: 'Gasoline',
    exteriorColor: '',
    interiorColor: ''
  });

  useEffect(() => {
    // Check auth
    if (localStorage.getItem('adminAuth') !== 'true') {
      navigate('/admin');
      return;
    }

    // Load vehicle if editing
    const loadVehicle = async () => {
      if (isEdit) {
        try {
          const response = await getVehicle(id);
          setFormData(response.data);
        } catch (error) {
          console.error('Error loading vehicle:', error);
        }
      }
    };
    
    loadVehicle();
  }, [id, isEdit, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      // Clean up data
      const vehicleData = {
        ...formData,
        price: parseInt(formData.price),
        mileage: parseInt(formData.mileage),
        year: parseInt(formData.year),
        images: formData.images.filter(img => img.trim() !== ''),
        features: formData.features.filter(f => f.trim() !== '')
      };

      if (isEdit) {
        // Update existing
        await updateVehicle(id, vehicleData);
      } else {
        // Add new
        await createVehicle(vehicleData);
      }

      toast({
        title: isEdit ? "Vehicle Updated" : "Vehicle Added",
        description: `Successfully ${isEdit ? 'updated' : 'added'} the vehicle.`,
      });

      navigate('/admin/dashboard');
    } catch (error) {
      console.error('Error saving vehicle:', error);
      toast({
        title: "Error",
        description: "Failed to save vehicle. Please try again.",
        variant: "destructive"
      });
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleArrayChange = (field, index, value) => {
    const newArray = [...formData[field]];
    newArray[index] = value;
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const addArrayItem = (field) => {
    setFormData(prev => ({ ...prev, [field]: [...prev[field], ''] }));
  };

  const removeArrayItem = (field, index) => {
    const newArray = formData[field].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [field]: newArray }));
  };

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;
    
    await uploadFiles(files);
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    
    const files = Array.from(e.dataTransfer.files);
    await uploadFiles(files);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const uploadFiles = async (files) => {
    if (files.length === 0) return;
    
    // Validate file types
    const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
    const invalidFiles = files.filter(file => !validTypes.includes(file.type));
    
    if (invalidFiles.length > 0) {
      toast({
        title: "Invalid File Type",
        description: "Please upload only JPG, PNG, GIF, or WebP images.",
        variant: "destructive"
      });
      return;
    }
    
    setUploading(true);
    
    try {
      const response = await uploadImages(files);
      const uploadedUrls = response.data.urls;
      
      // Prepend backend URL to each image path
      const backendUrl = process.env.REACT_APP_BACKEND_URL;
      const fullUrls = uploadedUrls.map(url => `${backendUrl}${url}`);
      
      // Add uploaded URLs to images array
      setFormData(prev => ({
        ...prev,
        images: [...prev.images.filter(img => img.trim() !== ''), ...fullUrls]
      }));
      
      toast({
        title: "Upload Successful",
        description: `${files.length} image(s) uploaded successfully.`,
      });
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: "Upload Failed",
        description: error.response?.data?.detail || "Failed to upload images. Please try again.",
        variant: "destructive"
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link to="/admin/dashboard">
            <Button variant="ghost" className="text-teal-600 hover:text-teal-700">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Dashboard
            </Button>
          </Link>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card>
          <CardContent className="p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">
              {isEdit ? 'Edit Vehicle' : 'Add New Vehicle'}
            </h1>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Make *
                  </label>
                  <Input
                    required
                    value={formData.make}
                    onChange={(e) => handleChange('make', e.target.value)}
                    placeholder="BMW, Mercedes, Audi..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Model *
                  </label>
                  <Input
                    required
                    value={formData.model}
                    onChange={(e) => handleChange('model', e.target.value)}
                    placeholder="5 Series, E-Class..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Year *
                  </label>
                  <Input
                    type="number"
                    required
                    value={formData.year}
                    onChange={(e) => handleChange('year', e.target.value)}
                    min="1990"
                    max={new Date().getFullYear() + 1}
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Price ($) *
                  </label>
                  <Input
                    type="number"
                    required
                    value={formData.price}
                    onChange={(e) => handleChange('price', e.target.value)}
                    placeholder="45000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mileage *
                  </label>
                  <Input
                    type="number"
                    required
                    value={formData.mileage}
                    onChange={(e) => handleChange('mileage', e.target.value)}
                    placeholder="25000"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Condition *
                  </label>
                  <Select value={formData.condition} onValueChange={(value) => handleChange('condition', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Excellent">Excellent</SelectItem>
                      <SelectItem value="Certified Pre-Owned">Certified Pre-Owned</SelectItem>
                      <SelectItem value="Very Good">Very Good</SelectItem>
                      <SelectItem value="Good">Good</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Transmission *
                  </label>
                  <Select value={formData.transmission} onValueChange={(value) => handleChange('transmission', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Automatic">Automatic</SelectItem>
                      <SelectItem value="Manual">Manual</SelectItem>
                      <SelectItem value="CVT">CVT</SelectItem>
                      <SelectItem value="Single Speed">Single Speed</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Fuel Type *
                  </label>
                  <Select value={formData.fuelType} onValueChange={(value) => handleChange('fuelType', value)}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Gasoline">Gasoline</SelectItem>
                      <SelectItem value="Diesel">Diesel</SelectItem>
                      <SelectItem value="Electric">Electric</SelectItem>
                      <SelectItem value="Hybrid">Hybrid</SelectItem>
                      <SelectItem value="Plug-in Hybrid">Plug-in Hybrid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Exterior Color *
                  </label>
                  <Input
                    required
                    value={formData.exteriorColor}
                    onChange={(e) => handleChange('exteriorColor', e.target.value)}
                    placeholder="Black, White, Silver..."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Interior Color *
                  </label>
                  <Input
                    required
                    value={formData.interiorColor}
                    onChange={(e) => handleChange('interiorColor', e.target.value)}
                    placeholder="Black Leather, Tan..."
                  />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Description *
                </label>
                <Textarea
                  required
                  value={formData.description}
                  onChange={(e) => handleChange('description', e.target.value)}
                  placeholder="Describe the vehicle..."
                  rows={4}
                />
              </div>

              {/* Images */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vehicle Images
                </label>
                
                {/* Drag & Drop Upload Area */}
                <div 
                  className="mb-4"
                  onDrop={handleDrop}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                >
                  <label className="cursor-pointer block">
                    <div className={`border-2 border-dashed rounded-lg p-8 text-center transition-all ${
                      isDragging 
                        ? 'border-teal-600 bg-teal-100 scale-105' 
                        : 'border-teal-300 bg-teal-50 hover:border-teal-500 hover:bg-teal-100'
                    }`}>
                      <div className="flex flex-col items-center">
                        {uploading ? (
                          <>
                            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mb-4"></div>
                            <p className="text-sm text-gray-700 font-medium">Uploading images...</p>
                          </>
                        ) : (
                          <>
                            <Upload className="h-12 w-12 text-teal-600 mb-3" />
                            <p className="text-lg text-gray-900 font-bold mb-2">
                              {isDragging ? 'Drop images here' : 'Drag & Drop images here'}
                            </p>
                            <p className="text-sm text-gray-600 mb-2">
                              or click to browse
                            </p>
                            <p className="text-xs text-gray-500">
                              JPG, PNG, GIF, WebP • Max 10MB per file • Multiple files supported
                            </p>
                          </>
                        )}
                      </div>
                    </div>
                    <input
                      type="file"
                      multiple
                      accept="image/jpeg,image/jpg,image/png,image/gif,image/webp"
                      onChange={handleImageUpload}
                      disabled={uploading}
                      className="hidden"
                    />
                  </label>
                </div>

                {/* Image Preview */}
                {formData.images.filter(img => img.trim() !== '').length > 0 && (
                  <div className="mb-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">
                      Uploaded Images ({formData.images.filter(img => img.trim() !== '').length})
                    </p>
                    <div className="grid grid-cols-3 gap-4">
                      {formData.images.filter(img => img.trim() !== '').map((image, index) => (
                        <div key={index} className="relative group">
                          <img 
                            src={image} 
                            alt={`Vehicle ${index + 1}`}
                            className="w-full h-32 object-cover rounded-lg border-2 border-gray-200"
                          />
                          <button
                            type="button"
                            onClick={() => {
                              const newImages = formData.images.filter((_, i) => i !== index);
                              setFormData(prev => ({ ...prev, images: newImages }));
                            }}
                            className="absolute top-2 right-2 bg-red-600 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            <X className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Manual URL Entry */}
                <div className="border-t pt-4">
                  <p className="text-sm font-medium text-gray-700 mb-2">Or add image URLs manually</p>
                  <div className="space-y-3">
                    {formData.images.map((image, index) => (
                      <div key={index} className="flex gap-2">
                        <Input
                          value={image}
                          onChange={(e) => handleArrayChange('images', index, e.target.value)}
                          placeholder="https://example.com/image.jpg"
                        />
                        {formData.images.length > 1 && (
                          <Button
                            type="button"
                            variant="outline"
                            size="icon"
                            onClick={() => removeArrayItem('images', index)}
                          >
                            <X className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => addArrayItem('images')}
                    >
                      <Plus className="mr-2 h-4 w-4" />
                      Add URL
                    </Button>
                  </div>
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Features
                </label>
                <div className="space-y-3">
                  {formData.features.map((feature, index) => (
                    <div key={index} className="flex gap-2">
                      <Input
                        value={feature}
                        onChange={(e) => handleArrayChange('features', index, e.target.value)}
                        placeholder="Navigation System, Leather Seats..."
                      />
                      {formData.features.length > 1 && (
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => removeArrayItem('features', index)}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  ))}
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => addArrayItem('features')}
                  >
                    <Plus className="mr-2 h-4 w-4" />
                    Add Feature
                  </Button>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-4 pt-6">
                <Button
                  type="submit"
                  size="lg"
                  className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
                >
                  {isEdit ? 'Update Vehicle' : 'Add Vehicle'}
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="lg"
                  onClick={() => navigate('/admin/dashboard')}
                >
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
